import React, { useState, useEffect } from 'react';
import { 
  Users, ShoppingCart, MessageSquare, Settings, BarChart3, Bell, 
  Search, Filter, Download, Plus, Edit, Trash2, Eye, Moon, Sun,
  TrendingUp, Activity, DollarSign, Star, AlertTriangle, CheckCircle,
  Clock, Globe, Zap, Target, Database, Shield, Coffee, Store
} from 'lucide-react';

const AdminDashboard = () => {
  const [darkMode, setDarkMode] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [notifications, setNotifications] = useState(12);
  const [searchQuery, setSearchQuery] = useState('');

  // Mock data for the dashboard
  const stats = {
    totalUsers: 24876,
    activeUsers: 18234,
    totalOrders: 3456,
    revenue: 234567,
    discussions: 892,
    feedback: 156,
    suggestions: 89
  };

  const recentActivity = [
    { id: 1, type: 'user', action: 'New user registration', user: 'Sarah Chen', time: '2 min ago' },
    { id: 2, type: 'order', action: 'Order completed', user: 'Mike Johnson', time: '5 min ago' },
    { id: 3, type: 'feedback', action: 'Feedback received', user: 'Emma Davis', time: '8 min ago' },
    { id: 4, type: 'discussion', action: 'New discussion started', user: 'Alex Rodriguez', time: '12 min ago' }
  ];

  const bgClass = darkMode 
    ? 'bg-gradient-to-br from-slate-900 via-blue-900 to-emerald-900/20' 
    : 'bg-gradient-to-br from-blue-50 via-emerald-50 to-white';

  const StatCard = ({ title, value, icon: Icon, trend, color }) => (
    <div className={`${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white/80 border-gray-200'} 
      backdrop-blur-sm border rounded-xl p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1`}>
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{title}</p>
          <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{value}</p>
          {trend && (
            <div className="flex items-center mt-2">
              <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
              <span className="text-sm text-green-500">+{trend}%</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  const NavigationItem = ({ icon: Icon, label, section, badge }) => (
    <button
      onClick={() => setActiveSection(section)}
      className={`w-full flex items-center px-4 py-3 rounded-lg transition-all duration-200 group
        ${activeSection === section 
          ? (darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
          : (darkMode ? 'text-gray-400 hover:text-white hover:bg-slate-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100')
        }`}
    >
      <Icon className="w-5 h-5 mr-3" />
      <span className="font-medium">{label}</span>
      {badge && (
        <span className={`ml-auto px-2 py-1 text-xs rounded-full ${
          darkMode ? 'bg-red-600 text-white' : 'bg-red-500 text-white'
        }`}>
          {badge}
        </span>
      )}
    </button>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard 
                title="Total Users" 
                value={stats.totalUsers.toLocaleString()} 
                icon={Users} 
                trend={12}
                color="bg-gradient-to-r from-blue-500 to-blue-600"
              />
              <StatCard 
                title="Active Users" 
                value={stats.activeUsers.toLocaleString()} 
                icon={Activity} 
                trend={8}
                color="bg-gradient-to-r from-green-500 to-green-600"
              />
              <StatCard 
                title="Total Orders" 
                value={stats.totalOrders.toLocaleString()} 
                icon={ShoppingCart} 
                trend={15}
                color="bg-gradient-to-r from-purple-500 to-purple-600"
              />
              <StatCard 
                title="Revenue" 
                value={`$${stats.revenue.toLocaleString()}`} 
                icon={DollarSign} 
                trend={23}
                color="bg-gradient-to-r from-emerald-500 to-emerald-600"
              />
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className={`${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white/80 border-gray-200'} 
                backdrop-blur-sm border rounded-xl p-6`}>
                <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Recent Activity
                </h3>
                <div className="space-y-4">
                  {recentActivity.map((activity) => (
                    <div key={activity.id} className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        activity.type === 'user' ? 'bg-blue-100' :
                        activity.type === 'order' ? 'bg-green-100' :
                        activity.type === 'feedback' ? 'bg-yellow-100' : 'bg-purple-100'
                      }`}>
                        {activity.type === 'user' && <Users className="w-4 h-4 text-blue-600" />}
                        {activity.type === 'order' && <ShoppingCart className="w-4 h-4 text-green-600" />}
                        {activity.type === 'feedback' && <Star className="w-4 h-4 text-yellow-600" />}
                        {activity.type === 'discussion' && <MessageSquare className="w-4 h-4 text-purple-600" />}
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {activity.action}
                        </p>
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {activity.user} • {activity.time}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className={`${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white/80 border-gray-200'} 
                backdrop-blur-sm border rounded-xl p-6`}>
                <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Platform Health
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Server Status</span>
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm text-green-500">Online</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Database</span>
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm text-green-500">Connected</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>API Response</span>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 text-yellow-500 mr-2" />
                      <span className="text-sm text-yellow-500">245ms</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Security</span>
                    <div className="flex items-center">
                      <Shield className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm text-green-500">Secure</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'users':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                User Management
              </h2>
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center">
                <Plus className="w-4 h-4 mr-2" />
                Add User
              </button>
            </div>
            
            <div className={`${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white/80 border-gray-200'} 
              backdrop-blur-sm border rounded-xl p-6`}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                      <th className={`text-left py-3 px-4 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>User</th>
                      <th className={`text-left py-3 px-4 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Status</th>
                      <th className={`text-left py-3 px-4 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Join Date</th>
                      <th className={`text-left py-3 px-4 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1,2,3,4,5].map(i => (
                      <tr key={i} className={`border-b ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                        <td className={`py-3 px-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          User {i}
                        </td>
                        <td className="py-3 px-4">
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">Active</span>
                        </td>
                        <td className={`py-3 px-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          2024-01-{10 + i}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex space-x-2">
                            <button className="text-blue-500 hover:text-blue-700">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button className="text-green-500 hover:text-green-700">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button className="text-red-500 hover:text-red-700">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      
      case 'ecommerce':
        return (
          <div className="space-y-6">
            <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              E-Commerce Management
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <StatCard 
                title="Total Orders" 
                value="3,456" 
                icon={ShoppingCart} 
                trend={15}
                color="bg-gradient-to-r from-blue-500 to-blue-600"
              />
              <StatCard 
                title="Pending Orders" 
                value="89" 
                icon={Clock} 
                color="bg-gradient-to-r from-yellow-500 to-yellow-600"
              />
              <StatCard 
                title="Completed Orders" 
                value="3,367" 
                icon={CheckCircle} 
                color="bg-gradient-to-r from-green-500 to-green-600"
              />
            </div>
            
            <div className={`${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white/80 border-gray-200'} 
              backdrop-blur-sm border rounded-xl p-6`}>
              <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Recent Orders
              </h3>
              <div className="space-y-4">
                {[1,2,3,4].map(i => (
                  <div key={i} className={`flex items-center justify-between p-4 rounded-lg ${
                    darkMode ? 'bg-slate-700/50' : 'bg-gray-50'
                  }`}>
                    <div>
                      <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Order #{1000 + i}
                      </p>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Customer {i} • $99.99
                      </p>
                    </div>
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                      Processing
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="space-y-6">
            <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {activeSection.charAt(0).toUpperCase() + activeSection.slice(1)} Management
            </h2>
            <div className={`${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white/80 border-gray-200'} 
              backdrop-blur-sm border rounded-xl p-6`}>
              <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {activeSection} management interface will be implemented here.
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className={`min-h-screen ${bgClass} transition-all duration-500`}>
      <div className="flex">
        {/* Sidebar */}
        <div className={`w-64 ${darkMode ? 'bg-slate-800/80' : 'bg-white/80'} backdrop-blur-sm border-r ${
          darkMode ? 'border-slate-700' : 'border-gray-200'
        } min-h-screen`}>
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-lg flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h1 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                PersonaAdmin
              </h1>
            </div>
            
            <nav className="space-y-2">
              <NavigationItem icon={BarChart3} label="Overview" section="overview" />
              <NavigationItem icon={Users} label="User Management" section="users" />
              <NavigationItem icon={MessageSquare} label="Discussions" section="discussions" badge="5" />
              <NavigationItem icon={Database} label="Content Management" section="content" />
              <NavigationItem icon={Target} label="Suggestions" section="suggestions" />
              <NavigationItem icon={ShoppingCart} label="E-Commerce" section="ecommerce" />
              <NavigationItem icon={Coffee} label="Fast Food" section="fastfood" />
              <NavigationItem icon={Star} label="Feedback" section="feedback" badge="12" />
              <NavigationItem icon={Settings} label="Settings" section="settings" />
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {/* Header */}
          <header className={`${darkMode ? 'bg-slate-800/80' : 'bg-white/80'} backdrop-blur-sm border-b ${
            darkMode ? 'border-slate-700' : 'border-gray-200'
          } px-6 py-4`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`} />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`pl-10 pr-4 py-2 rounded-lg border ${
                      darkMode 
                        ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                </div>
                <button className={`p-2 rounded-lg ${
                  darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'
                }`}>
                  <Filter className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                </button>
              </div>
              
              <div className="flex items-center space-x-4">
                <button className={`p-2 rounded-lg ${
                  darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'
                }`}>
                  <Download className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                </button>
                <button className={`relative p-2 rounded-lg ${
                  darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'
                }`}>
                  <Bell className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                  {notifications > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {notifications}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setDarkMode(!darkMode)}
                  className={`p-2 rounded-lg ${
                    darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'
                  }`}
                >
                  {darkMode ? (
                    <Sun className="w-5 h-5 text-yellow-500" />
                  ) : (
                    <Moon className="w-5 h-5 text-gray-600" />
                  )}
                </button>
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full"></div>
              </div>
            </div>
          </header>

          {/* Main Content Area */}
          <main className="p-6">
            {renderContent()}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;